import { Avatar } from '@/components/common/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
} from '@/components/ui/sidebar';
import { NetworkStatusBanner } from '@/hooks/use-network-status';
import { cn } from '@/lib/utils';
import { extractNameFromEmail } from '@/lib/utils/string';
import { useAuthActions } from '@convex-dev/auth/react';
import { useQuery } from 'convex/react';
import { ChevronDownIcon, LogOutIcon, SettingsIcon } from 'lucide-react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '~/convex/_generated/api';
import { useNavigation } from '../../hooks/use-navigation';
import type { ClockIconHandle } from '../ui/clock';
import { ClockIcon } from '../ui/clock';
import type { FileTextIconHandle } from '../ui/file-text';
import { FileTextIcon } from '../ui/file-text';
import type { HomeIconHandle } from '../ui/home';
import { HomeIcon } from '../ui/home';

interface DesktopNavigationProps {
  className?: string;
}

export function DesktopNavigation({ className }: DesktopNavigationProps) {
  const { t } = useTranslation();
  const { outlet, isActive, handleNavigate } = useNavigation();
  const currentUser = useQuery(api.user.currentUser);
  const { signOut } = useAuthActions();
  const displayName =
    currentUser?.name || extractNameFromEmail(currentUser?.email);

  const homeIconRef = useRef<HomeIconHandle>(null);
  const clockIconRef = useRef<ClockIconHandle>(null);
  const fileTextIconRef = useRef<FileTextIconHandle>(null);

  const stopAllAnimations = (ignore: 'home' | 'reports' | 'templates') => {
    if (ignore !== 'home') {
      homeIconRef.current?.stopAnimation();
    }
    if (ignore !== 'reports') {
      clockIconRef.current?.stopAnimation();
    }
    if (ignore !== 'templates') {
      fileTextIconRef.current?.stopAnimation();
    }
  };

  const handleSignOut = async () => {
    await signOut();
    handleNavigate('/', true);
  };

  return (
    <SidebarProvider
      className={cn('hidden flex-1 md:flex', className)}
      defaultOpen={true}>
      <Sidebar collapsible="none" className="h-dvh justify-between">
        <SidebarHeader className="mb-4 max-w-[200px] p-6">
          <img
            onClick={() => handleNavigate('/', true)}
            src="/logo-text.svg"
            alt="Voicefill"
            className="ease-out-quad w-full cursor-pointer transition-all duration-150 hover:opacity-70"
          />
        </SidebarHeader>
        <SidebarContent>
          <SidebarGroup>
            <SidebarMenu className="gap-2">
              <SidebarMenuItem>
                <SidebarMenuButton
                  className="text-gray-500"
                  onClick={() => {
                    stopAllAnimations('home');
                    homeIconRef.current?.startAnimation();
                    handleNavigate('/', true);
                  }}
                  isActive={isActive('/')}>
                  <HomeIcon
                    ref={homeIconRef}
                    className={cn('size-5!', isActive('/') && 'stroke-primary')}
                  />
                  {t('home:title')}
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className="text-gray-500"
                  onClick={() => {
                    stopAllAnimations('reports');
                    clockIconRef.current?.startAnimation();
                    handleNavigate('/reports', true);
                  }}
                  isActive={isActive('/reports')}>
                  <ClockIcon
                    ref={clockIconRef}
                    className={cn(
                      'size-5!',
                      isActive('/reports') && 'stroke-primary',
                    )}
                  />
                  {t('report:title')}
                </SidebarMenuButton>
              </SidebarMenuItem>

              <SidebarMenuItem>
                <SidebarMenuButton
                  className="text-gray-500"
                  onClick={() => {
                    stopAllAnimations('templates');
                    fileTextIconRef.current?.startAnimation();
                    handleNavigate('/templates', true);
                  }}
                  isActive={isActive('/templates')}>
                  <FileTextIcon
                    ref={fileTextIconRef}
                    className={cn(
                      'size-5!',
                      isActive('/templates') && 'stroke-primary',
                    )}
                  />
                  {t('templates:title')}
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroup>
        </SidebarContent>
        <SidebarFooter>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <div className="flex w-full cursor-pointer items-center justify-between rounded-md px-3 py-2 transition-all duration-200 ease-out hover:bg-gray-100">
                <div className="flex items-center gap-3 overflow-x-hidden text-left">
                  <Avatar
                    avatarUrl={currentUser?.image}
                    name={currentUser?.name}
                    email={currentUser?.email}
                    size="sm"
                    className="shrink-0"
                  />
                  <span className="w-full flex-1 truncate text-sm font-medium text-gray-900">
                    {displayName}
                  </span>
                </div>
                <ChevronDownIcon className="size-4" />
              </div>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem
                className="cursor-pointer"
                onClick={() => handleNavigate('/settings', true)}>
                <SettingsIcon className="size-4" />
                {t('settings:title')}
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                className="cursor-pointer"
                onClick={handleSignOut}>
                <LogOutIcon className="text-destructive size-4" />
                {t('settings:signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </SidebarFooter>
      </Sidebar>
      <SidebarInset>
        <NetworkStatusBanner />
        {outlet}
      </SidebarInset>
    </SidebarProvider>
  );
}
